const _ = require("lodash");

const ElevShared = require("./ElevShared");

/**
 * The table of Beast Builder colors. This object maps "#rrggbb" hex colors to
 * arrays giving data about that color. The possible values of the array are:
 * - ["palette", col, row]: A color on the main palette. The palette is a grid
 *   of several columns of hues and rows of luminosities. The user can change
 *   the hue of their part, replacing colors from this grid with those of a
 *   different column while keeping the row fixed.
 * - ["global", name]: A global color used by several parts, unaffected by
 *   the recoloring.
 * - ["invis"]: An apparent unused export artifact of content team's tools.
 *   These are intended as stopgaps until content fixes things on their end,
 *   and any invis entries in this table should get removed eventually.
 */
const colorTable = require("./data/beastColors.json");

/**
 * Maximum number of parts allowed.
 */
const PART_LIMIT = 100;

/**
 * Minimum and maximum values for the shade part of a color palette key.
 */
const PALETTE_SHADE_MIN = 1;
const PALETTE_SHADE_MAX = 4;

/**
 * Trainer / Beast Builder aspect ratio. Copied from ContainerSize.js.
 */
const ASPECT_RATIO = 4 / 3;

/**
 * Position values for the movable area (.content) of Beast Builder, relative
 * to trainer container element. Given as 0-1 values that are rendered as
 * CSS percentages.
 */
const BB_CONTENT_DIMS = {
	left: 0.208,
	top: 0.12,
	width: 0.584,
	height: 0.584 * ASPECT_RATIO,
};

/**
 * Position values for the circle (.avatarBg) of Beast Builder, relative
 * to trainer container element. Given as 0-1 values that are rendered as
 * CSS percentages.
 */
const BB_CIRCLE_DIMS = {
	left: 0.27,
	top: 0.22,
	width: 0.46,
	height: 0.46 * ASPECT_RATIO,
};

/**
 * Ratio of image size to circle diameter for a circle that fits snugly inside
 * the avatar ring asset (assets/images/icons/button-surround.svg).
 */
const RING_DIAMETER = 0.788;

/**
 * Validation for a part to be saved in the database.
 */
const isValidPart = function(part) {
	const allowedKeys = ["id", "n", "s", "p", "r", "f", "c"];
	return (
		_.every(_.keys(part), k => _.includes(allowedKeys, k)) &&
		_.isFinite(part.id) &&
		_.isFinite(part.n) &&
		_.isFinite(part.s) &&
		Array.isArray(part.p) &&
		_.isFinite(part.p[0]) &&
		_.isFinite(part.p[1]) &&
		_.isFinite(part.r) &&
		(_.isFinite(part.f) || typeof part.f === "boolean") &&
		Array.isArray(part.c) &&
		(!part.c[0] || _.isFinite(part.c[0])) &&
		_.isFinite(part.c[1])
	);
};

/**
 * Rearrangement of the palette colors from the table as an object mapping
 * `${col},${row}` to the "#rrggbb" hex code. This object is exported from this
 * file and is used by the frontend recoloring machinery.
 *
 * @returns {object}
 */
const getColorPalette = function() {
	const colorPalette = {};
	_.each(colorTable, (val, key) => {
		if (val[0] === "palette") {
			colorPalette[`${val[1]},${val[2]}`] = key;
		}
	});
	return colorPalette;
};

/**
 * Get the default color of a part as a [scheme, shade] pair. Returns null if
 * the part has no palette colors.
 *
 * @param {object} inventory Object where inventory.parts maps partIDs to beast
 *     part data.
 * @param {number} partID
 * @returns {null | [number, number]}
 */
const getDefaultPartColor = function(inventory, partID) {
	const part = _.get(inventory, ["parts", partID, "content"]);
	if (!part || !part.defaultScheme) return null;
	return [part.defaultScheme, part.shades[0] || 2];
};

/**
 * Get allowed values for the shadeOffset prop in the beast part component.
 * This should be any value that, if added to each shade used in the part,
 * keeps those shades contained in the palette shade min-max range (see
 * constants above).
 *
 * @param {object} inventory Object where inventory.parts maps partIDs to beast
 *     part data.
 * @param {number} partID
 * @returns {number[]}
 */
const getShadeOffsets = function(inventory, partID) {
	const part = _.get(inventory, ["parts", partID, "content"]);
	if (!part || !part.defaultScheme) return [0];
	const min = _.min(part.shades);
	const max = _.max(part.shades);
	return _.range(PALETTE_SHADE_MIN - min, PALETTE_SHADE_MAX - max + 1);
};

/**
 * Get the URL that displays a user avatar given their avatarData.
 *
 * @param {object} avatarData The object in the user model avatarData field.
 *     Currently supports {color, num} for pre Beast Builder default avatars
 *     or {hash, userID} for Beast Builder avatars.
 * @return {string}
 */
const getAvatarURL = ElevShared.getAvatarURL;

/**
 * Determine whether the provided avatar data is valid for the given user ID.
 * Used by user updating endpoints as validation.
 */
const validateAvatarData = function(userID, avatarData) {
	if (avatarData.num) {
		return true;
	} else if (avatarData.userID === userID || avatarData.userID === 1) {
		return true;
	}
	return false;
};

/**
 * Get the non Beast Builder avatar off an avatarData object, if any. Returns
 * null if none found.
 *
 * @param {object} avatarData The object in the user model avatarData field.
 * @return {object}
 */
const getNonBBAvatar = function(avatarData) {
	if (avatarData.num) return _.pick(avatarData, ["num", "color"]);
	if (avatarData.nonBB) return avatarData.nonBB;
	return null;
};

const AVATAR_SCHEME = ElevShared.AVATAR_SCHEME;

const getRandomBeastColorForUser = ElevShared.getRandomBeastColorForUser;

const getDefaultAvatarNameForUser = ElevShared.getDefaultAvatarNameForUser;

const getDefaultAvatarFilenames = ElevShared.getDefaultAvatarFilenames;

const getDefaultBeastForUser = (userID, scheme) => {
	const c = !scheme ? 10 : getRandomBeastColorForUser(userID, scheme);
	return {
		parts: [
			{
				c: [null, 0],
				f: 0,
				n: 2,
				p: [0.389, 0.379],
				r: 0,
				s: 0.195,
				id: 200,
			},
			{
				c: [c, 0],
				f: 0,
				n: 2,
				p: [0.248, 0.311],
				r: 0,
				s: 0.5,
				id: 34,
			},
		],
	};
};

module.exports = {
	PALETTE_SHADE_MIN,
	PALETTE_SHADE_MAX,
	BB_CONTENT_DIMS,
	BB_CIRCLE_DIMS,
	RING_DIAMETER,
	PART_LIMIT,
	isValidPart,
	getColorPalette,
	getDefaultPartColor,
	getShadeOffsets,
	AVATAR_SCHEME,
	getAvatarURL,
	validateAvatarData,
	getNonBBAvatar,
	getDefaultAvatarNameForUser,
	getDefaultAvatarFilenames,
	getDefaultBeastForUser,
};
